import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Clock, RotateCcw, ShieldCheck, Star } from "lucide-react";
import { FavoriteButton } from "@/components/marketplace/favorite-button";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { fetchMarketplaceService, formatUsd } from "@/lib/marketplace/queries";
import { MarketplaceOrderPanel } from "@/components/marketplace/order-panel";
import { buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/$username/$serviceSlug")({
  head: ({ params }) =>
    buildPageMetadata({
      path: `/marketplace/${params.username}/${params.serviceSlug}`,
      title: `${params.serviceSlug.replace(/-/g, " ")} by ${params.username} | BeHot.Now Marketplace`,
      description: `Book "${params.serviceSlug.replace(/-/g, " ")}" from ${params.username} on BeHot.Now: fixed packages, clear delivery times and protected payments.`,
      ogType: "product",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: params.username, path: `/marketplace/${params.username}/${params.serviceSlug}` },
      ],
    }),
  component: ServicePage,
});

function ServicePage() {
  const { t } = useT();
  const { username, serviceSlug } = Route.useParams();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["mp-service", username, serviceSlug],
    queryFn: () => fetchMarketplaceService(username, serviceSlug),
  });

  if (isLoading) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-6xl space-y-4 px-4 py-10">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-72 w-full" />
        </div>
      </MarketplaceGate>
    );
  }

  if (!data) {
    return (
      <MarketplaceGate>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">{t("mp.service.not_found")}</h1>
          <Button asChild variant="hot" className="mt-6">
            <Link to="/marketplace">{t("mp.service.back_to_marketplace")}</Link>
          </Button>
        </div>
      </MarketplaceGate>
    );
  }

  const { service, seller, packages, extras, reviews } = data;
  const activePackage = packages.find((p) => p.id === selectedPackage) ?? packages[0];
  const sellerName = seller.display_name || seller.handle || t("mp.common.seller");

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div>
            <h1 className="font-display text-2xl font-bold sm:text-3xl">{service.title}</h1>
            <div className="mt-3 flex items-center gap-3">
              <Avatar className="size-9">
                <AvatarImage src={seller.avatar_url ?? undefined} />
                <AvatarFallback>{sellerName.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <Link
                  to="/user/$slug"
                  params={{ slug: seller.slug || seller.handle || seller.id }}
                  search={{ ref: undefined }}
                  className="text-sm font-semibold hover:underline"
                >
                  {sellerName}
                </Link>
                {service.rating_count > 0 && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Star className="size-3.5 fill-amber-400 text-amber-400" />
                    {Number(service.rating_avg).toFixed(1)} · {t("mp.service.reviews_count", { n: service.rating_count })}
                  </div>
                )}
              </div>
            </div>

            {service.cover_url && (
              <img
                src={service.cover_url}
                alt={service.title}
                className="mt-6 aspect-video w-full rounded-xl object-cover"
              />
            )}

            <section className="mt-8">
              <h2 className="font-display text-lg font-bold">{t("mp.service.about")}</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {service.description || service.short_description}
              </p>
            </section>

            {extras.length > 0 && (
              <section className="mt-8">
                <h2 className="font-display text-lg font-bold">{t("mp.service.addons")}</h2>
                <ul className="mt-3 space-y-2">
                  {extras.map((x) => (
                    <li key={x.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                      <span>
                        {x.name}
                        {x.extra_delivery_days > 0 && (
                          <span className="text-muted-foreground"> · +{t("mp.order.days", { n: x.extra_delivery_days })}</span>
                        )}
                      </span>
                      <span className="font-semibold">+{formatUsd(x.price_cents)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-8">
              <h2 className="font-display text-lg font-bold">{t("mp.service.reviews")}</h2>
              {reviews.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">{t("mp.service.no_reviews")}</p>
              ) : (
                <ul className="mt-3 space-y-4">
                  {reviews.map((r) => (
                    <li key={r.id} className="rounded-lg border border-border p-4">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`size-4 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-muted"}`}
                          />
                        ))}
                      </div>
                      {r.body && <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="card-surface p-5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="size-4 text-primary" />
                {t("mpcopy.fees.protected")}
              </div>
              <div className="mt-4 space-y-3">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPackage(p.id)}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${
                      activePackage?.id === p.id ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold capitalize">{p.tier.toLowerCase()}</span>
                      <span className="font-display text-lg font-bold">{formatUsd(p.price_cents)}</span>
                    </div>
                    {p.description && <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>}
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="size-3.5" /> {t("mp.order.days", { n: p.delivery_days })}
                      </span>
                      <span className="flex items-center gap-1">
                        <RotateCcw className="size-3.5" /> {t("mp.service.revisions_count", { n: p.revisions })}
                      </span>
                    </div>
                  </button>
                ))}
                {packages.length === 0 && (
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{t("mp.service.starting_at")}</span>
                      <span className="font-display text-lg font-bold">{formatUsd(service.starting_price_cents)}</span>
                    </div>
                  </div>
                )}
              </div>
              <MarketplaceOrderPanel
                serviceId={service.id}
                packageId={activePackage?.id}
                packageLabel={
                  activePackage
                    ? `${activePackage.name || activePackage.tier} · ${formatUsd(activePackage.price_cents)}`
                    : formatUsd(service.starting_price_cents)
                }
                extras={extras}
              />
              <FavoriteButton
                serviceId={service.id}
                initialCount={service.favorites_count ?? 0}
                className="mt-2 w-full"
              />

              <p className="mt-3 text-center text-[11px] text-muted-foreground">
                {t("mpcopy.fees.buyer")} {t("mp.service.at_checkout")} · {t("mpcopy.fees.seller")} {t("mp.service.paid_by_seller")}
              </p>
            </div>
            {service.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {service.tags.map((t: string) => (
                  <Badge key={t} variant="secondary">
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </MarketplaceGate>
  );
}
